import { TextSelection } from 'prosemirror-state'
import { nanoid } from 'nanoid'

/**
 * Validates and fixes `list-id`s on `customListItem` nodes in a ProseMirror document.
 *
 * Consider 2 lists, with differing formats.  One list-id=`X` and the other list-id=`Y`:
 *
 * 1. X
 * 2. X
 *
 * I. Y
 * II. Y
 *
 * Using the text editor, you could imagine a user copying and pasting elements
 * from each list, resulting in a structure like this:
 *
 *   1. X
 *   I. Y
 *   2. X
 *   II. Y  ❌ (Invalid - Y breaks out of its slice of X.  This makes it impossible
 *              to recreate in the form of Google Docs batch updates.)
 *
 * This function ensures that all custom lists nest cleanly within one another. It prevents
 * interleaved structures like the one above.
 *
 * Once corrected, the resulting structure is:
 *
 *   1. X
 *   I. Y
 *   2. X
 *   I. Z  ✅ (Valid - Y is now fully contained within a slice of X)
 *
 * Structures like the ❌ example are generally fine except that they are
 * impossible to recreate in the form of Google Docs batch updates.  The API
 * simply does not allow it. The best we can do is allow for nested lists that
 * adhere to the property that, if they are nested in some "slice" of a list,
 * then they must be fully contained within that slice.
 *
 * "Nested" here refers less to the idea of a list being inside another list,
 * and more to the idea of a list being fully contained within a "slice" of
 * another list. The tabbing in these examples is very intentional.  These are
 * 2 lists mixed together, not 1 list with strange formatting. This is not
 * traditional nesting, but rather a way to allow for users to insert
 * paragraphs, images, or other content in between list items, including other
 * lists to a limited extent, "breaking out of the list," so to speak.
 *
 * "Slice" here refers to a space in between 2 list items:
 *
 *   1. X
 *        < --- slice --- >
 *   2. X
 *
 * Any number of lists or whatever else can be placed in that slice, but any lists
 * that are placed in a slice must be fully contained within that slice, and they
 * may not break out of it.  This applies recursively, as well.  So long as every
 * list inside some slice is fully contained within that slice, it is valid.
 *
 * This function mutates the provided transaction to update the `list-id`s such that
 * the proper nesting and contiguity is maintained in cases where the UI allows
 * for invalid structures to be created.
 *
 * @param tr - The ProseMirror transaction to mutate.
 * @returns The updated transaction or `null` if no changes were made.
 */

export function ensureDistributedListIds(tr) {
  const { anchor, head } = tr.selection
  const correctionMap = generateCustomListCorrectionMarkup(tr.doc)
  let hasChanges = false
  const positionsToUpdate = Array.from(correctionMap.keys()).sort(
    (a, b) => a - b,
  )
  for (const pos of positionsToUpdate) {
    const attrs = correctionMap.get(pos)
    if (attrs) {
      tr.setNodeMarkup(pos, undefined, attrs)
      hasChanges = true
    }
  }
  if (hasChanges) {
    tr.setSelection(TextSelection.create(tr.doc, anchor, head))
  }
  return hasChanges ? tr : null
}

function generateCustomListCorrectionMarkup(doc, correctionMap = new Map()) {
  const listIdToRangeMap = new Map()

  doc.descendants((node, nodePos) => {
    if (node.type.name === 'customListItem') {
      const listId = node.attrs['list-id']
      if (!listIdToRangeMap.has(listId)) {
        listIdToRangeMap.set(listId, { start: nodePos, end: nodePos })
      }
      const currentRange = listIdToRangeMap.get(listId)
      currentRange.start = Math.min(currentRange.start, nodePos)
      currentRange.end = Math.max(currentRange.end, nodePos + node.nodeSize)
    }
  })

  let openListIdStack = []
  let openListIdSet = new Set()
  const reassignedListIdMap = {}

  doc.descendants((node, nodePos) => {
    const attrs = {
      ...node.attrs,
      ...(correctionMap.get(nodePos) || {}),
    }
    for (let i = 0; i < openListIdStack.length; i++) {
      let currentListId = openListIdStack[i]
      const currentRange = listIdToRangeMap.get(currentListId)
      if (!(nodePos >= currentRange.start && nodePos < currentRange.end)) {
        openListIdStack.splice(i, 1)
        openListIdSet.delete(currentListId)
        i--
      }
    }

    if (node.type.name === 'customListItem') {
      let resolvedListId = attrs['list-id']
      while (reassignedListIdMap[resolvedListId]) {
        resolvedListId = reassignedListIdMap[resolvedListId]
      }
      if (openListIdSet.has(resolvedListId)) {
        while (openListIdStack[openListIdStack.length - 1] !== resolvedListId) {
          const nestedListId = openListIdStack.pop()
          const originalRange = listIdToRangeMap.get(nestedListId)
          const splitOffRange = {
            start: nodePos,
            end: originalRange.end,
          }
          originalRange.end = nodePos
          openListIdSet.delete(nestedListId)
          // After this point, we will use a new list id in place of this nested
          // list's id, preventing it from potentially breaking out of its slice.
          // If encountered in the future, this id will resolve to this new id.
          reassignedListIdMap[nestedListId] = nanoid()
          listIdToRangeMap.set(reassignedListIdMap[nestedListId], splitOffRange)
        }
      }
      if (openListIdStack[openListIdStack.length - 1] !== resolvedListId) {
        openListIdStack.push(resolvedListId)
        openListIdSet.add(resolvedListId)
      }
      if (node.attrs['list-id'] !== resolvedListId) {
        attrs['list-id'] = resolvedListId
        correctionMap.set(nodePos, attrs)
      }
    }
    return false
  })
  return correctionMap
}
